/***
 * A module that performs AJAX requests and makes handling the most common types of responses easier.
 *
 * Example usages:

SCM.Ajax.get('/url', { qs1: 'value1' })
    // request URL will be '/url?qs1=value1'
    .then(function(result) {
      // result is a string with the response content
    })
    .catch(function(reason)) {
      // if the request failed for any reason
      //
      // reason is an instance of AjaxError
    });

SCM.Ajax.post('/url', { qs1: 'value1' })
    // request will POST FormData with qs1 = 'value1'
    .expectHtml()
    .then(function(result) {
      // result is a string with (assumed Html) response
    })
    .catch(function(reason)) {
      // if the request failed for any reason
      //
      // reason is an instance of AjaxError
    });

SCM.Ajax.get('/url')
    .expectJson()
    .then(function(result) {
      // result is an object parsed from the response JSON string
    })
    .catch(function(reason)) {
      // if the request failed for any reason
      // or if the response failed to parse as JSON
      //
      // reason is an instance of AjaxError
    });

SCM.Ajax.get('/url')
    .expectActionResultModel()
    .then(function(result) {
      // result is an object with the structure of the ActionResultModel class, parsed from the response JSON string
    })
    .catch(function(reason)) {
      // if the request failed for any reason
      // or if the response failed to parse as JSON
      // or if the response does not look like a ActionResultModel object
      //
      // reason is an instance of AjaxError
    });

SCM.Ajax.get('/url')
    .expectActionResultModel()
    .expectSuccess()
    .then(function(result) {
      // result is a boolean that is always true
    })
    .catch(function(reason)) {
      // if the request failed for any reason
      // or if the response failed to parse as JSON
      // or if the response does not look like a ActionResultModel object
      // or if response.Success is false
      //
      // reason is an instance of AjaxError
    });

SCM.Ajax.get('/url')
    .expectActionResultModel()
    .expectHtml()
    .then(function(result) {
      // result is a string with the value of ActionResultModel.Html, parsed from the response JSON string
    })
    .catch(function(reason)) {
      // if the request failed for any reason
      // or if the response failed to parse as JSON
      // or if the response does not look like a ActionResultModel object
      // or if response.Success is false
      // or if response.Html is not defined
      //
      // reason is an instance of AjaxError
    });

SCM.Ajax.get('/url')
    .expectActionResultModel()
    .expectData()
    .then(function(result) {
      // result is an object with the structure of whatever class was assigned to ActionResultModel.Data,
      // parsed from the response JSON string
    })
    .catch(function(reason)) {
      // if the request failed for any reason
      // or if the response failed to parse as JSON
      // or if the response does not look like a ActionResultModel object
      // or if response.Success is false
      // or if response.Data is not defined
      //
      // reason is an instance of AjaxError
    });

 */

/**
 * @typedef {object} ActionResultModel
 * @property {boolean} Success
 * @property {string} Html
 * @property {string} RedirectUrl
 * @property {string} ReturnId
 * @property {object} Data
 */
/**
 * @typedef {object} AjaxError
 * @property {string} message
 * @property {string} userFriendlyMessage
 * @property {ActionResultModel} actionResultModel
 */
/**
 * @typedef {object} InternalAjaxResult
 * @property {Promise<string|BlobResult|object>} promise
 * @property {AbortController|jqXHR} abortableRequestHandle
 */
/**
 * @typedef {object} BlobResult
 * @property {blob} blob
 * @property {string} fileName
 * @property {string} message
 */

var SCM = SCM || {};
SCM.Ajax = (function ($) {
    var moduleDefinition = {
        get: _get,
        post: _post,
        ajax: _ajax,
        // These methods are exposed for unit testing only
        _objectToFormData: _objectToFormData,
        _formatPostData: _formatPostData
    };

    /**
     * 
     * @param {string} url
     * @param {object} data
     * @returns {AjaxResult} An object that implements the {Promise} interface and can also be used to
     *     restrict the result to a particular expected return type
     */
    function _get(url, data) {
        return new AjaxResult(url, 'GET', data);
    }

    /**
     *
     * @param {string} url
     * @param {object} data
     * @returns {AjaxResult} An object that implements the {Promise} interface and can also be used to
     *     restrict the result to a particular expected return type
     */
    function _post(url, data) {
        return new AjaxResult(url, 'POST', data);
    }

    /**
     *
     * @param {string} url
     * @param {string} method
     * @param {object} data
     * @returns {AjaxResult} An object that implements the {Promise} interface and can also be used to
     *     restrict the result to a particular expected return type
     */
    function _ajax(url, method, data) {
        return new AjaxResult(url, method, data);
    }

    var _resultType = {
        Text: 'text',
        Html: 'html',
        Json: 'json',
        Xml: 'xml',
        ActionResultModel: {
            Full: 'actionResultModelFull',
            FullEnsureSuccess: 'actionResultModelFullEnsureSuccess',
            Success: 'actionResultModelSuccess',
            Html: 'actionResultModelHtml',
            RedirectUrl: 'actionResultModelRedirectUrl',
            ReturnId: 'actionResultModelReturnId',
            Data: 'actionResultModelData'
        },
        BlobResult: 'blob'
    };

    var _requestContentType = {
        Default: 'Default',
        Json: 'application/json; charset=utf-8',
        UrlEncoded: 'application/x-www-form-urlencoded'
    };

    /**
     * @constructor
     * @param {string} url
     * @param {string} method
     * @param {object} data
     * @param {string} dataType
     * @returns {AjaxResult}
     * @description Represents an Ajax response that can be further restricted to a particular expected data type.
     *     Default expected data type is 'text'
     *     Implements the {Promise<any>} interface.
     */
    function AjaxResult(url, method, data, dataType) {
        this._url = url;
        this._method = method || 'GET';
        this._data = data;
        this._dataType = dataType || _resultType.Text;
        this._internalPromise = null;
        this._requestContentType = _requestContentType.Default;
        this._abortableRequestHandle = null;
    }
    /* Promise interface */
    /**
     * Receives callbacks for the resolution and/or rejection of the internal Promise.
     * @param {(value: any) => any} onFulfilled
     * @param {(reason: any) => any} [onRejected]
     * @returns {AjaxResult} which implements the {Promise} interface
     */
    AjaxResult.prototype.then = function (onFulfilled, onRejected) {
        if (!this._internalPromise) {
            var internalAjaxResult = _ajaxInternal(this._url, this._method, this._data, this._dataType, this._requestContentType);
            this._internalPromise = internalAjaxResult.promise;
            this._abortableRequestHandle = internalAjaxResult.abortableRequestHandle;
        }
        this._internalPromise = this._internalPromise
            .then(onFulfilled, onRejected);
        return this;
    };
    /**
     * Receives a callback for only the rejection of the internal Promise.
     * @param {(reason: any) => any} [onRejected]
     * @returns {AjaxResult} which implements the {Promise} interface
     */
    AjaxResult.prototype.catch = function (onRejected) {
        if (!this._internalPromise) {
            var internalAjaxResult = _ajaxInternal(this._url, this._method, this._data, this._dataType, this._requestContentType);
            this._internalPromise = internalAjaxResult.promise;
            this._abortableRequestHandle = internalAjaxResult.abortableRequestHandle;
        }
        this._internalPromise = this._internalPromise
            .catch(onRejected);
        return this;
    };
    /**
     * Receives a callback that is invoked when the Promise is settled (either fulfilled or rejected).
     * The resolved value cannot be accessed or modified.
     * @param {() => void} onFinally
     * @returns {AjaxResult} which implements the {Promise} interface
     */
    AjaxResult.prototype.finally = function (onFinally) {
        if (!this._internalPromise) {
            var internalAjaxResult = _ajaxInternal(this._url, this._method, this._data, this._dataType, this._requestContentType);
            this._internalPromise = internalAjaxResult.promise;
            this._abortableRequestHandle = internalAjaxResult.abortableRequestHandle;
        }
        this._internalPromise = this._internalPromise
            .finally(onFinally);
        return this;
    };
    /* Send Content Type methods */
    AjaxResult.prototype.sendAsJson = function () {
        this._requestContentType = _requestContentType.Json;
        return this;
    };
    AjaxResult.prototype.sendAsUrlEncoded = function () {
        this._requestContentType = _requestContentType.UrlEncoded;
        return this;
    };
    /**
     * Aborts the currently active AJAX request initiated by this instance.
     * Returns the original {AjaxResult}.
     * @returns {AjaxResult} which implements the {Promise<object>} interface.
     */
    AjaxResult.prototype.abort = function () {
        _abortRequest(this._abortableRequestHandle);
        return this;
    };

    /* Expect methods */
    /**
     * Returns a {AjaxResult}-wrapped resolved Promise whose value is the object parsed from JSON
     * contained in the response.
     * Returns a {AjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if the response content cannot be parsed as JSON.
     * @returns {AjaxResult} which implements the {Promise<object>} interface
     */
    AjaxResult.prototype.expectJson = function () {
        this._dataType = _resultType.Json;
        return this.then(function (res) { return res; });
    };
    /**
     * Returns a {AjaxResult}-wrapped resolved Promise whose value is the string contents contained in the response.
     * Returns a {AjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if there was a problem with the response.
     * @returns {AjaxResult} which implements the {Promise<string>} interface
     */
    AjaxResult.prototype.expectHtml = function () {
        this._dataType = _resultType.Html;
        return this.then(function (res) { return res; });
    };
    /**
     * Returns a {AjaxResult}-wrapped resolved Promise whose value is the string contents contained in the response.
     * Returns a {AjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if there was a problem with the response.
     * @returns {AjaxResult} which implements the {Promise<string>} interface
     */
    AjaxResult.prototype.expectText = function () {
        this._dataType = _resultType.Text;
        return this.then(function (res) { return res; });
    };
    /**
     * Not Implemented
     * @throws {Exception} not implemented
     */
    AjaxResult.prototype.expectXml = function () {
        this._dataType = _resultType.Xml;
        return this.then(function (res) { return res; });
    };
    /**
     * Ensure that the result satisfies a custom check
     * @param {string} [failureMessage] a custom message to use for the AjaxError if the condition specified
     *  in the expectFunc is not met.
     * @param {(result) => boolean} expectFunc a function that takes a single parameter, which will be the
     *  result from the AJAX requests, and returns true or false
     * @returns {AjaxResult} whose internal Promise is resolved with the expected result if {expectFunc}
     *  returns true; or whose internal Promise is rejected with an {AjaxError} if {expectFunc} returns false
     */
    AjaxResult.prototype.expect = function (failureMessage, expectFunc) {
        if (typeof failureMessage === 'function') {
            expectFunc = failureMessage;
            failureMessage = null;
        }
        return this.then(function (res) {
            if (!expectFunc(res)) {
                throw new AjaxError(failureMessage || 'Unexpected result from server');
            }
            return res;
        });
    };
    AjaxResult.prototype.expectBlob = function () {
        this._dataType = _resultType.BlobResult;
        return this.then(function (res) { return res; });
    };
    /**
     * Returns a {AjaxResult}-wrapped resolved Promise whose value is the ActionResultModel
     * object parsed from JSON contained in the response.
     * Returns a {AjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if the response content cannot be parsed as JSON
     * or does not appear to be structured like the SCM.SharedControls.Models.ActionResultModel class.
     * @returns {ActionResultAjaxResult}
     */
    AjaxResult.prototype.expectActionResultModel = function () {
        this._dataType = _resultType.Json;
        return new ActionResultAjaxResult(this);
    };


    /**
     * @constructor
     * @param {AjaxResult} ajaxResult
     * @returns {ActionResultAjaxResult}
     * @description Represents an Ajax response that can be further restricted to one of the particular
     *     fields of the SCM.SharedControls.Models.ActionResultModel class.
     *     Implements the {Promise<ActionResultModel>} interface.
     */
    function ActionResultAjaxResult(ajaxResult) {
        this._ajaxResult = ajaxResult;
        this._expectedComponent = _resultType.ActionResultModel.Full;
        this._internalPromise = null;
    }
    /* Promise interface */
    /**
     * Receives callbacks for the resolution and/or rejection of the internal Promise.
     * @param {(value: any) => any} onFulfilled
     * @param {(reason: any) => any} [onRejected]
     * @returns {ActionResultAjaxResult} which implements the {Promise} interface
     */
    ActionResultAjaxResult.prototype.then = function (onFulfilled, onRejected) {
        this._internalPromise =
            (this._internalPromise || _handleActionResultModel(this._ajaxResult, this._expectedComponent))
                .then(onFulfilled, onRejected);
        return this;
    };
    /**
     * Receives a callback for only the rejection of the internal Promise.
     * @param {(reason: any) => any} onRejected
     * @returns {ActionResultAjaxResult} which implements the {Promise} interface
     */
    ActionResultAjaxResult.prototype.catch = function (onRejected) {
        this.intrnalPromise =
            (this._internalPromise || _handleActionResultModel(this._ajaxResult, this._expectedComponent))
                .catch(onRejected);
        return this;
    };
    /**
     * Receives a callback that is invoked when the Promise is settled (either fulfilled or rejected).
     * The resolved value cannot be accessed or modified.
     * @param {() => void} onFinally
     * @returns {ActionResultAjaxResult} which implements the {Promise} interface
     */
    ActionResultAjaxResult.prototype.finally = function (onFinally) {
        this._internalPromise =
            (this._internalPromise || _handleActionResultModel(this._ajaxResult, this._expectedComponent))
                .finally(onFinally);
        return this;
    };
    /* Expect methods */
    ActionResultAjaxResult.prototype.ensureSuccess = function () {
        this._expectedComponent = _resultType.ActionResultModel.FullEnsureSuccess;
        return this;
    };
    /**
     * Returns a {ActionResultAjaxResult}-wrapped resolved Promise if .Success is true.
     * Returns a {ActionResultAjaxResult}-wrapped rejected Promise whose reason is an AjaxError if .Success is false.
     * @returns {ActionResultAjaxResult} which implements the {Promise<boolean>} interface.
     * The result will always be {true} if the Promise is in a resolved state.
     */
    ActionResultAjaxResult.prototype.expectSuccess = function () {
        this._expectedComponent = _resultType.ActionResultModel.Success;
        return this;
    };
    /**
     * Returns a {ActionResultAjaxResult}-wrapped resolved Promise whose value is the value of .Html
     * if .Success is true and .Html is defined.
     * Returns a {ActionResultAjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if .Success is false or .Html is not defined.
     * @returns {ActionResultAjaxResult} which implements the {Promise<string>} interface
     */
    ActionResultAjaxResult.prototype.expectHtml = function () {
        this._expectedComponent = _resultType.ActionResultModel.Html;
        return this;
    };
    /**
     * Returns a {ActionResultAjaxResult}-wrapped resolved Promise whose value is the value of .RedirectUrl
     * if .Success is true and .RedirectUrl is defined.
     * Returns a {ActionResultAjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if .Success is false or .RedirectUrl is not defined.
     * @returns {ActionResultAjaxResult} which implements the {Promise<string>} interface.
     */
    ActionResultAjaxResult.prototype.expectRedirectUrl = function () {
        this._expectedComponent = _resultType.ActionResultModel.RedirectUrl;
        return this;
    };
    /**
     * Returns a {ActionResultAjaxResult}-wrapped resolved Promise whose value is the value of .ReturnId
     * if .Success is true and .ReturnId is defined.
     * Returns a {ActionResultAjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if .Success is false or .ReturnId is not defined.
     * @returns {ActionResultAjaxResult} which implements the {Promise<string>} interface.
     */
    ActionResultAjaxResult.prototype.expectReturnId = function () {
        this._expectedComponent = _resultType.ActionResultModel.ReturnId;
        return this;
    };
    /**
     * Returns a {ActionResultAjaxResult}-wrapped resolved Promise whose value is the value of .Data
     * if .Success is true and .Data is defined.
     * The resolved value will have the structure of whatever class was set to the Data attribute on the server side.
     * Returns a {ActionResultAjaxResult}-wrapped rejected Promise whose reason is an AjaxError
     * if .Success is false or .Data is not defined.
     * @returns {ActionResultAjaxResult} which implements the {Promise<object>} interface.
     */
    ActionResultAjaxResult.prototype.expectData = function () {
        this._expectedComponent = _resultType.ActionResultModel.Data;
        return this;
    };
    /**
     * Aborts the currently active AJAX request initiated by this instance.
     * Returns the original {ActionResultAjaxResult}.
     * @returns {ActionResultAjaxResult} which implements the {Promise<object>} interface.
     */
    ActionResultAjaxResult.prototype.abort = function () {
        this._ajaxResult && this._ajaxResult.abort();
        return this;
    };


    /**
     * 
     * @param {string} message
     * @param {string} userFriendlyMessage
     * @param {ActionResultModel} actionResultModel
     * @returns {AjaxError}
     */
    function AjaxError(message, userFriendlyMessage, actionResultModel) {
        var instance = new Error(message);
        instance.name = 'AjaxError';
        instance.userFriendlyMessage = userFriendlyMessage;
        instance.actionResultModel = actionResultModel;
        Object.setPrototypeOf(instance, Object.getPrototypeOf(this));
        if (Error.captureStackTrace) {
            Error.captureStackTrace(instance, AjaxError);
        }
        return instance;
    }
    AjaxError.prototype = Object.create(Error.prototype, {
        constructor: {
            value: Error,
            enumerable: false,
            writable: true,
            configurable: true
        }
    });
    if (Object.setPrototypeOf) {
        Object.setPrototypeOf(AjaxError, Error);
    } else {
        AjaxError.__proto__ = Error;
    }


    /**
     * 
     * @param {string} url
     * @param {string} method
     * @param {object} data
     * @param {string} dataType
     * @returns {InternalAjaxResult} An object that contains a Promise that resolves to a result
     * whose type depends on the value of dataType; and a handler for aborting the request
     */
    function _ajaxInternal(url, method, data, dataType, contentType) {
        if (_browserImplementsFetchApi()) {
            return _ajaxWithFetch(url, method, data, dataType, contentType);
        } else if (_jQueryIsAvailable()) {
            return _ajaxWithJquery(url, method, data, dataType, contentType);
        } else {
            throw new Error('No supported AJAX implementations found.');
        }
    }

    /**
     *
     * @param {string} url
     * @param {string} method
     * @param {object} data
     * @param {string} dataType
     * @returns {InternalAjaxResult} An object that contains a Promise that resolves to a result
     * whose type depends on the value of dataType; and a handler for aborting the request
     */
    function _ajaxWithFetch(url, method, data, dataType, contentType) {
        var abortController = new AbortController();
        if (method === 'GET' && data) {
            url = _appendToUrl(url, data);
            data = undefined;
        }
        var initParams = {
            method: method,
            body: _formatPostData(data, contentType),
            signal: abortController.signal
        };
        initParams.headers = _getFetchHeaders(url, contentType);
        var promise = fetch(url, initParams)
            .then(function (response) {
                if (!response.ok) {
                    throw new AjaxError(
                        'Bad response from server: ' + response.status + ': ' + response.statusText,
                        'Bad response from server');
                }
                if (dataType === _resultType.Json) {
                    return response.json();
                }
                if (dataType === _resultType.BlobResult) {
                    var contentDisposition = response.headers.get('Content-Disposition');
                    var fileName = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : 'unknownFileName';                    

                    return response.blob().then(blob => ({ blob: blob, fileName: fileName, message: response.statusText }));
                }
                return response.text();
            })
            .catch(function(reason) {
                throw new AjaxError(
                    'Request failed: ' + reason,
                    'Request failed');
            });
        return {
            promise: promise,
            abortableRequestHandle: abortController
        };
    }

    /**
     *
     * @param {string} url
     * @param {string} method
     * @param {object} data
     * @param {string} dataType
     * @returns {InternalAjaxResult} An object that contains a Promise that resolves to a result
     * whose type depends on the value of dataType; and a handler for aborting the request
     */
    function _ajaxWithJquery(url, method, data, dataType, contentType) {
        var ajaxSettings = { url: url, method: method, type: method, data: data, dataType: dataType };
        if (_isFormDataWithFile(data)) {
            ajaxSettings.contentType = false;
            ajaxSettings.processData = false;
        }
        if (_shouldSetContentTypeHeader(contentType)) {
            ajaxSettings.contentType = contentType;
        }
        var jqXhr;
        var promise = new Promise(function(resolutionFunc, rejectionFunc) {
            jqXhr = $.ajax(ajaxSettings)
                .done(function (result) {
                    resolutionFunc(result);
                })
                .fail(function (xhr, status, error) {
                    var errorMessage = status === 'parsererror'
                        ? 'Failed to parse response as ' + dataType
                        : error;
                    rejectionFunc(new AjaxError(
                        errorMessage,
                        'Request failed'));
                });
        });
        return {
            promise: promise,
            abortableRequestHandle: jqXhr
        };
    }

    function _isFormDataWithFile(data) {
        return data instanceof FormData &&
            Array.from(data)
                .filter(function (d) { return d.length > 1 && d[1] instanceof File; })
                .length;
    }

    /**
     * 
     * @param {string} url
     * @param {string|object} objectOrQueryString
     */
    function _appendToUrl(url, objectOrQueryString) {
        var qs = objectOrQueryString;
        if (typeof qs === 'object') {
            qs = _objectToQueryString(qs);
        }
        if (qs && qs.length && ['?', '&'].includes(qs[0])) {
            qs = qs.substring(1);
        }
        return url.includes('?')
            ? url + '&' + qs
            : url + '?' + qs;
    }

    /**
     * 
     * @param {object} obj
     * @returns {string}
     */
    function _objectToQueryString(obj) {
        return Object.keys(obj)
            .map(function (key) { return key + '=' + obj[key]; })
            .join('&');
    }

    /**
     * 
     * @param {object} obj
     * @returns {FormData}
     */
    function _objectToFormData(obj) {
        if (!obj) {
            return undefined;
        }
        if (obj instanceof FormData || typeof obj !== 'object') {
            return obj;
        }
        var formData = new FormData();
        _setFormDataValues(formData, obj);
        return formData;
    }

    function _setFormDataValues(formData, obj, prefix) {
        formData = formData || new FormData();

        if (Array.isArray(obj)) {
            for (var i = 0; i < obj.length; i++) {
                var arrPrefix = prefix + '[' + i + ']';
                _setFormDataValues(formData, obj[i], arrPrefix);
            }
        } else if (_isDate(obj)) {
            formData.append(prefix, obj.toISOString());
        } else if (_isObject(obj) && !_isBlob(obj) && !_isFile(obj)) {
            Object.keys(obj)
                .forEach(function(key) {
                    var objPrefix = prefix ? prefix + '[' + key + ']' : key;
                    _setFormDataValues(formData, obj[key], objPrefix);
                });
        } else if (obj !== undefined && obj !== null){
            formData.append(prefix, obj);
        }

    }

    function _isObject(value) { return value === Object(value); }
    function _isDate(value) { return value instanceof Date; }

    function _isBlob(value) {
        return value &&
            typeof value.size === 'number' &&
            typeof value.type === 'string' &&
            typeof value.slice === 'function';
    }

    function _isFile(value) {
        return _isBlob(value) &&
            typeof value.name === 'string' &&
            (typeof value.lastModifiedDate === 'object' ||
                typeof value.lastModified === 'number');
    }

    function _shouldSetContentTypeHeader(contentType) {
        return contentType && contentType === _requestContentType.Json;
    }

    function _formatPostData(data, format) {
        switch (format) {
            case _requestContentType.UrlEncoded:
                return new URLSearchParams(data);
            case _requestContentType.Json:
                return JSON.stringify(data);
            default:
                return _objectToFormData(data);
        }
    }

    function _browserImplementsFetchApi() {
        return typeof window.fetch === 'function';
    }

    function _jQueryIsAvailable() {
        return typeof $ === 'function' && $() && $().jquery;
    }

    function _getFetchHeaders(url, contentType) {
        var headers = {};
        if (_isSameOrigin(url)) {
            headers['X-Requested-With'] = 'XMLHttpRequest';
            headers['X-Requested-With-Lies'] =
                'Not actually XMLHttpRequest, but Fetch API via SCM.Ajax; X-Requested-With is set for server-side backwards compatibility';
        }
        if (_shouldSetContentTypeHeader(contentType)) {
            headers['Content-Type'] = contentType;
        }
        return headers;
    }

    function _isSameOrigin(url) {
        var currentOrigin = window.location.origin;
        var targetOrigin = new URL(url, currentOrigin).origin;
        return currentOrigin === targetOrigin;
    }

    /**
     * 
     * @param {Promise<any>} responsePromise
     * @param {string} resultType
     * @returns {Promise<any>}
     */
    function _handleActionResultModel(responsePromise, resultType) {
        var validResponsePromise = _checkForValidResponse(responsePromise);

        if (resultType === _resultType.ActionResultModel.Full) {
            return validResponsePromise;
        }

        var successPromise = _checkForSuccess(validResponsePromise);

        if (resultType === _resultType.ActionResultModel.FullEnsureSuccess) {
            return successPromise;
        }

        if (resultType === _resultType.ActionResultModel.Success) {
            return successPromise.then(function () { return true; });
        }

        if (resultType === _resultType.ActionResultModel.Html) {
            return _checkForHtml(successPromise);
        }

        if (resultType === _resultType.ActionResultModel.RedirectUrl) {
            return _checkForRedirectUrl(successPromise);
        }

        if (resultType === _resultType.ActionResultModel.ReturnId) {
            return _checkFormReturnId(successPromise);
        }

        if (resultType === _resultType.ActionResultModel.Data) {
            return _checkForData(successPromise);
        }

        return successPromise;
    }

    /**
     * 
     * @param {Promise<any>} responsePromise
     * @returns {Promise<ActionResultModel>}
     */
    function _checkForValidResponse(responsePromise) {
        return responsePromise
            .then(function (result) {
                if (!result || result.Success === undefined) {
                    throw new AjaxError(
                        'Response does not appear to be an ActionResultModel',
                        'Unexpected response from server');
                }
                return result;
            });
    }

    /**
     *
     * @param {Promise<ActionResultModel>} responsePromise
     * @returns {Promise<ActionResultModel>}
     */
    function _checkForSuccess(validResponsePromise) {
        return validResponsePromise
            .then(function (result) {
                if (!result.Success) {
                    var message = 'Unspecified error';
                    if (_hasMessages(result)) {
                        message = result.Messages[0];
                    } else if (_hasValidationErrors(result)) {
                        message = result.ValidationErrors[0].Errors[0];
                    } else if (result.Html) {
                        // Yes, we sometimes send an error message in the Html field
                        message = result.Html;
                    }
                    throw new AjaxError(
                        message,
                        'Request was not successful',
                        result);
                }
                return result;
            });
    }

    /**
     *
     * @param {Promise<ActionResultModel>} responsePromise
     * @returns {Promise<string>}
     */
    function _checkForHtml(successResponsePromise) {
        return successResponsePromise
            .then(function (response) {
                if (typeof response.Html !== 'string') {
                    throw new AjaxError(
                        'No HTML was provided in the response',
                        'Unexpected response from server',
                        response);
                }
                return response.Html;
            });
    }

    /**
     *
     * @param {Promise<ActionResultModel>} responsePromise
     * @returns {Promise<string>}
     */
    function _checkForRedirectUrl(successResponsePromise) {
        return successResponsePromise
            .then(function (response) {
                if (typeof response.RedirectUrl !== 'string') {
                    throw new AjaxError(
                        'No RedirectUrl was provided in the response',
                        'Unexpected response from server',
                        response);
                }
                return response.RedirectUrl;
            });
    }

    /**
     *
     * @param {Promise<ActionResultModel>} responsePromise
     * @returns {Promise<string>}
     */
    function _checkFormReturnId(successResponsePromise) {
        return successResponsePromise
            .then(function (response) {
                if (typeof response.ReturnId !== 'string') {
                    throw new AjaxError(
                        'No ReturnId was provided in the response',
                        'Unexpected response from server',
                        response);
                }
                return response.ReturnId;
            });
    }

    /**
     *
     * @param {Promise<ActionResultModel>} responsePromise
     * @returns {Promise<object>}
     */
    function _checkForData(successResponsePromise) {
        return successResponsePromise
            .then(function (response) {
                if (response.Data === undefined || response.Data === null || typeof response.Data !== 'object') {
                    throw new AjaxError(
                        'No Data object was provided in the response',
                        'Unexpected response from server',
                        response);
                }
                return response.Data;
            });
    }

    /**
     * 
     * @param {ActionResultModel} result
     * @returns {boolean}
     */
    function _hasValidationErrors(result) {
        return result &&
            result.ValidationErrors &&
            result.ValidationErrors.length &&
            result.ValidationErrors[0].Errors &&
            result.ValidationErrors[0].Errors.length;
    }

    /**
     *
     * @param {ActionResultModel} result
     * @returns {boolean}
     */
    function _hasMessages(result) {
        return result && result.Messages && result.Messages.length;
    }

    function _abortRequest(abortableRequestHandle) {
        if (!abortableRequestHandle || !abortableRequestHandle.abort) {
            return;
        }

        abortableRequestHandle.abort();
    }

    return moduleDefinition;

})(window.jQuery || {});

// If we are in a module context, export this module (required for unit test)
'object' === typeof module && module.exports && (module.exports = SCM.Ajax);
